import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { StatusBadge, type Column } from "@fawkes/ui";
import { formatDateTime } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Het tabblad Documenten op een crematoriumklant: de documenten van al zijn uitvaarten bij elkaar
 * — opdrachtbevestigingen, certificaten, as-retourbrieven — met per document de uitvaart waar het
 * bij hoort, zodat je van hieruit het dossier opent.
 *
 * <p>Alleen lezen. Maken en versturen gebeurt vanuit het dossier zelf, waar de sjablonen en het
 * kanaal (print, e-mail) bij de uitvaart horen.
 */

interface KlantDocument {
  id: string;
  type: string | null;
  omschrijving: string | null;
  status: string | null;
  kanaal: string | null;
  aangemaaktOp: string | null;
  aangemaaktDoor: string | null;
  dossierId: string;
  dossierNummer: string | null;
  dierNaam: string | null;
}

const STATUS_TOON: Record<string, "success" | "info" | "neutral" | "warning"> = {
  VERZONDEN: "success",
  GEPRINT: "success",
  CONCEPT: "info",
  GEPLAND: "neutral",
  MISLUKT: "warning",
};

export function CrematoriumKlantDocumenten({ relatieId, onOpenDossier }: {
  relatieId: string;
  onOpenDossier?: (dossierId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading } = useCustom<KlantDocument[]>({
    url: `crematorium/relaties/${relatieId}/documenten`,
    method: "get",
  });
  const rijen = Array.isArray(data?.data) ? data!.data : [];

  const kolommen: Column<KlantDocument>[] = [
    {
      header: t("crematoriumKlantDocumenten.col.document"),
      cell: (d) => d.omschrijving || d.type || "—",
    },
    {
      header: t("crematoriumKlantDocumenten.col.categorie"),
      cell: (d) => d.type ?? "—",
    },
    {
      header: t("crematoriumKlantDocumenten.col.kanaal"),
      cell: (d) => d.kanaal ?? "—",
    },
    {
      header: t("crematoriumKlantDocumenten.col.datum"),
      cell: (d) => (d.aangemaaktOp ? formatDateTime(d.aangemaaktOp, locale) : "—"),
    },
    {
      header: t("crematoriumKlantDocumenten.col.uitvaart"),
      cell: (d) => (
        <button type="button" className="fawkes-link" onClick={() => onOpenDossier?.(d.dossierId)}>
          {[d.dossierNummer, d.dierNaam].filter(Boolean).join(" · ") || t("crematoriumKlantDocumenten.dossier")}
        </button>
      ),
    },
    {
      header: t("crematoriumKlantDocumenten.col.status"),
      cell: (d) => d.status
        ? <StatusBadge tone={STATUS_TOON[d.status] ?? "neutral"}>{d.status}</StatusBadge>
        : "—",
    },
  ];

  return (
    <IndeelbareTabel<KlantDocument> tabelKey="crematorium-klant-documenten"
      columns={kolommen}
      rows={rijen}
      getRowKey={(d) => d.id}
      empty={isLoading ? t("common.loading") : t("crematoriumKlantDocumenten.empty")}
    />
  );
}
